<template>
  <div>
    <!-- Overlay -->
    <transition name="fade">
      <div
        v-if="show"
        class="fixed inset-0 bg-black bg-opacity-50 z-40"
        @click="$emit('close')"
      ></div>
    </transition>

    <!-- Drawer -->
    <transition name="slide">
      <div
        v-if="show"
        class="fixed right-0 top-0 h-full w-80 md:w-96 bg-white shadow-xl z-50 flex flex-col"
      >
        <!-- Header -->
        <div class="flex items-center justify-between p-4 border-b bg-gray-50">
          <div class="flex items-center space-x-2">
            <svg
              version="1.1"
              class="w-5 h-5 text-red-500"
              viewBox="0 0 20 20"
            >
              <path
                fill="currentColor"
                stroke="currentColor"
                stroke-width="1"
                d="M10,4 C10,4 8.1,2 5.74,2 C3.38,2 1,3.55 1,6.73 C1,8.84 2.67,10.44 2.67,10.44 L10,18 L17.33,10.44 C17.33,10.44 19,8.84 19,6.73 C19,3.55 16.62,2 14.26,2 C11.9,2 10,4 10,4 L10,4 Z"
              ></path>
            </svg>
            <h3 class="text-lg font-medium text-gray-800">Favoriler</h3>
            <span class="bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full text-xs">
              {{ filteredFavorites.length }}
            </span>
          </div>
          <button
            @click="$emit('close')"
            class="p-1 hover:bg-gray-200 rounded-full transition-colors"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="w-5 h-5 text-gray-500"
              viewBox="0 0 24 24"
            >
              <path
                fill="currentColor"
                d="M6.4 19L5 17.6l5.6-5.6L5 6.4L6.4 5l5.6 5.6L17.6 5L19 6.4L13.4 12l5.6 5.6l-1.4 1.4l-5.6-5.6L6.4 19Z"
              />
            </svg>
          </button>
        </div>

        <!-- Search -->
        <div class="p-3 border-b">
          <div class="relative">
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Favorilerde ara..."
              class="w-full pl-9 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="w-4 h-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2"
              viewBox="0 0 24 24"
            >
              <path
                fill="currentColor"
                d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5A6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5Zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5S14 7.01 14 9.5S11.99 14 9.5 14Z"
              />
            </svg>
            <button
              v-if="searchQuery"
              @click="searchQuery = ''"
              class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M6.4 19L5 17.6l5.6-5.6L5 6.4L6.4 5l5.6 5.6L17.6 5L19 6.4L13.4 12l5.6 5.6l-1.4 1.4l-5.6-5.6L6.4 19Z"
                />
              </svg>
            </button>
          </div>
        </div>

        <!-- Favorites List -->
        <div class="flex-1 overflow-y-auto">
          <div v-if="filteredFavorites.length === 0" class="p-8 text-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="w-12 h-12 mx-auto text-gray-300 mb-3"
              viewBox="0 0 24 24"
            >
              <path
                fill="currentColor"
                d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5C2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3C19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
              />
            </svg>
            <p class="text-gray-500 text-sm">
              {{ searchQuery ? 'Arama sonucu bulunamadı' : 'Henüz favori eklenmemiş' }}
            </p>
          </div>

          <div v-else class="divide-y">
            <div
              v-for="(favorite, index) in paginatedFavorites"
              :key="favorite.id"
              class="group hover:bg-gray-50 transition-colors"
            >
              <div class="flex items-center justify-between p-3">
                <a
                  :href="favorite.url"
                  class="flex-1 min-w-0 pr-2"
                  @click="$emit('close')"
                >
                  <p class="text-sm text-gray-800 truncate hover:text-indigo-600 transition-colors">
                    {{ favorite.title }}
                  </p>
                  <p class="text-xs text-gray-400 truncate mt-0.5">
                    {{ formatUrl(favorite.url) }}
                  </p>
                </a>
                <button
                  @click="removeFavorite(favorite.id)"
                  class="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors opacity-0 group-hover:opacity-100"
                  title="Favorilerden kaldır"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="w-4 h-4"
                    viewBox="0 0 24 24"
                  >
                    <path
                      fill="currentColor"
                      d="M7 21q-.825 0-1.412-.587T5 19V6H4V4h5V3h6v1h5v2h-1v13q0 .825-.587 1.413T17 21H7Zm2-4h2V8H9v9Zm4 0h2V8h-2v9Z"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          <!-- Load More -->
          <div
            v-if="hasMoreItems"
            class="p-4 text-center border-t"
          >
            <button
              @click="loadMore"
              class="px-4 py-2 text-sm text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
            >
              Daha fazla göster ({{ remainingCount }} kaldı)
            </button>
          </div>
        </div>

        <!-- Footer -->
        <div class="p-3 border-t bg-gray-50 text-center">
          <p class="text-xs text-gray-500">
            Toplam {{ favorites.length }} favori
          </p>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'FavoritesDrawer',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    favorites: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      searchQuery: '',
      itemsPerPage: 50,
      currentPage: 1
    };
  },
  computed: {
    filteredFavorites() {
      if (!this.searchQuery) {
        return this.favorites;
      }
      const query = this.searchQuery.toLowerCase();
      return this.favorites.filter(f =>
        f.title.toLowerCase().includes(query) ||
        f.url.toLowerCase().includes(query)
      );
    },
    paginatedFavorites() {
      return this.filteredFavorites.slice(0, this.currentPage * this.itemsPerPage);
    },
    hasMoreItems() {
      return this.paginatedFavorites.length < this.filteredFavorites.length;
    },
    remainingCount() {
      return this.filteredFavorites.length - this.paginatedFavorites.length;
    }
  },
  watch: {
    show(val) {
      if (val) {
        this.searchQuery = '';
        this.currentPage = 1;
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    },
    searchQuery() {
      this.currentPage = 1;
    }
  },
  methods: {
    removeFavorite(id) {
      this.$emit('remove', id);
    },
    loadMore() {
      this.currentPage++;
    },
    formatUrl(url) {
      try {
        return url.replace(/^https?:\/\//, '').replace(/\/$/, '');
      } catch (e) {
        return url;
      }
    }
  },
  beforeDestroy() {
    document.body.style.overflow = '';
  }
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s ease;
}

.slide-enter,
.slide-leave-to {
  transform: translateX(100%);
}
</style>
